import { useEffect, useMemo, useState } from 'react'
import { Empty, Modal, Tooltip } from 'antd'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faArrowDown,
  faArrowUp,
  faCircleInfo,
  faFileLines,
  faPlusMinus,
} from '@fortawesome/free-solid-svg-icons'
import parse from 'html-react-parser'

import { HeaderTable } from '../../../../../../../../Components/CustomUi/table/headerTable'
import { BodyTable } from '../../../../../../../../Components/CustomUi/table/bodyTable'
import { Render } from '../../../../../../../../Components/CustomUi/render'
import { RowTable } from '../../../../../../../../Components/CustomUi/table/rowTable'
import { RowItem } from '../../../../../../../../Components/CustomUi/table/rowItem'
import { TitleH2 } from '../../../../../../../../Components/CustomUi/titleH2'
import { Inline } from '../../../../../../../../Components/CustomUi/inline'
import Tabs from '../../../../../../../../Components/Tabs'
import Carregando from '../../../../../../../../Components/CustomUi/loading'
import { Container, ContainerNaoInterpretadas, ContainerTextoCompleto } from './styled'
import {
  AbaInconsistencia,
  ehCabecalhoCategoria,
  formatarNumero,
  parseInconsistencias,
} from './meta/parseInconsistencias'

interface Props {
  texto: string
  abrir: boolean
  setAbrir: React.Dispatch<React.SetStateAction<boolean>>
}

export const VisualizarInconsistencia: React.FC<Props> = ({ texto, abrir, setAbrir }) => {
  const [tab_selecionado, setTab_selecionado] = useState<AbaInconsistencia>('Altas')
  const [loading, setLoading] = useState<boolean>(false)

  // O parsing é puro e barato: derivar direto do texto evita o estado
  // intermediário que ficava defasado quando `texto` mudava.
  const relatorio = useMemo(() => parseInconsistencias(texto), [texto])

  // 'Completo' mostra o texto cru do laboratório (como a produção); as outras
  // duas abas mostram a tabela estruturada da sua categoria.
  const modo_completo = tab_selecionado === 'Completo'

  const itens_da_aba = modo_completo
    ? []
    : relatorio.itens.filter((item) => item?.tipo === tab_selecionado)
  const nao_interpretadas_da_aba = modo_completo
    ? []
    : relatorio.nao_interpretadas[tab_selecionado] ?? []

  const aba_vazia = modo_completo
    ? relatorio.linhas_originais.length <= 0
    : itens_da_aba.length <= 0 && nao_interpretadas_da_aba.length <= 0

  useEffect(() => {
    if (abrir === false) {
      setTab_selecionado('Altas')
      return
    }

    setLoading(true)
    const timer = setTimeout(() => setLoading(false), 800)

    return () => clearTimeout(timer)
  }, [abrir])

  return (
    <Modal
      open={abrir}
      onCancel={() => setAbrir(false)}
      width={'600px'}
      destroyOnClose
      footer={false}
      zIndex={9999}
    >
      <Container>
        <Inline justifyContent='space-between'>
          <TitleH2>Resultado com inconsistência</TitleH2>
        </Inline>
        <Tabs
          tabs={[
            { icone: faArrowUp, descricao: 'Altas' },
            { icone: faPlusMinus, descricao: 'Moderadas' },
            { icone: faFileLines, descricao: 'Completo' },
          ]}
          selecionado={tab_selecionado}
          onChange={setTab_selecionado}
          trackWidth={180}
        />

        {/* O cabeçalho de colunas só faz sentido nas abas de tabela. */}
        <Render condition={!modo_completo}>
          <HeaderTable
            itens={[
              { valor: 'Elemento', width: '100px' },
              { valor: 'Profundidade', width: '120px' },
              { valor: 'Limite', width: '100px' },
              { valor: 'Valor', width: '100px' },
              { valor: 'Ponto', width: '100px' },
            ]}
          />
        </Render>

        <BodyTable
          style={{
            height: '300px',
            position: 'relative',
            overflowX: 'hidden',
            overflowY: 'scroll',
            marginTop: modo_completo ? 0 : -10,
          }}
        >
          <Render condition={loading}>
            <Carregando
              animation
              legenda='Carregando...'
              size='small'
              justifyContent='center'
              height={300}
            />
          </Render>

          <Render condition={loading === false}>
            {/* ABA COMPLETO — texto cru do laboratório, sem interpretação. */}
            <Render condition={modo_completo && !aba_vazia}>
              <ContainerTextoCompleto>
                {relatorio.linhas_originais.map((linha, index) => (
                  <p
                    className='linha__completa'
                    data-cabecalho={ehCabecalhoCategoria(linha)}
                    key={index}
                  >
                    {parse(linha)}
                  </p>
                ))}
              </ContainerTextoCompleto>
            </Render>

            {/* ABAS ALTAS / MODERADAS — tabela estruturada. */}
            {itens_da_aba.map(
              ({ elemento, valor, limite, limite_tipo, ponto, profundidade, original }, index) => {
                return (
                  <RowTable key={index} onDoubleClick={() => {}}>
                    <RowItem fontSize={13} width={'99px'}>
                      {elemento}
                    </RowItem>
                    <RowItem fontSize={12} width={'120px'}>
                      {profundidade ?? '-'}
                    </RowItem>
                    <RowItem fontSize={12} width={'100px'}>
                      {/* A seta diz QUAL limite foi rompido: teto (máximo) ou piso (mínimo). */}
                      <Tooltip title={original} placement='topLeft'>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                          <FontAwesomeIcon
                            icon={limite_tipo === 'maximo' ? faArrowUp : faArrowDown}
                            fontSize={11}
                            color={limite_tipo === 'maximo' ? '#ff4d4f' : '#2586f5'}
                          />
                          {formatarNumero(limite)}
                        </span>
                      </Tooltip>
                    </RowItem>
                    <RowItem fontSize={12} width={'100px'}>
                      {formatarNumero(valor)}
                    </RowItem>
                    <RowItem fontSize={12} width={'125px'}>
                      {ponto ?? '-'}
                    </RowItem>
                  </RowTable>
                )
              }
            )}

            {/* Fallback: o que a regex não estruturou aparece como texto, para que
                nenhuma inconsistência informada pelo laboratório seja escondida. */}
            <Render condition={nao_interpretadas_da_aba.length > 0}>
              <ContainerNaoInterpretadas>
                <div className='titulo__nao-interpretadas'>
                  <FontAwesomeIcon icon={faCircleInfo} fontSize={12} />
                  Demais observações do laboratório
                </div>
                {nao_interpretadas_da_aba.map((linha, index) => (
                  <p className='linha__nao-interpretada' key={index}>
                    {linha}
                  </p>
                ))}
              </ContainerNaoInterpretadas>
            </Render>

            <Render condition={aba_vazia}>
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={<> Nenhum registro disponível!</>}
              />
            </Render>
          </Render>
        </BodyTable>
      </Container>
    </Modal>
  )
}
